import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDocs, documentId } from 'firebase/firestore'; 
import { db } from '../../lib/firebase';
import { subscribeByIds } from '../../lib/subscribeByIds';
import StudyPlan from '../../components/StudyPlan';
import './student.css';
import Skeleton from '../../components/Skeleton';
import { useNavigate } from 'react-router-dom';
import WaitingRoom from '../../components/WaitingRoom'; 
import ExamList from './ExamList';
import NotificationBell from '../../components/NotificationBell';
import { motion, AnimatePresence } from 'framer-motion'; 

// --- HYBRID REPORT & AI SERVICE IMPORTS ---
import { downloadStudentAnalysisReport } from '../../utils/ReportGenerator';
import { generateHybridAnalysisContext } from '../../services/AnalyticsEngine';
import { fetchAiTutorInsights } from '../../services/AiService';

const StudentDashboard = ({ user, userData, setModal }) => {
    const navigate = useNavigate();
    
    // --- Existing Logic: Main View State ---
    const [dashboardTab, setDashboardTab] = useState('exams');
    
    // --- Existing Logic: Exam State ---
    const [view, setView] = useState('list'); 
    const [activeExam, setActiveExam] = useState(null);

    // --- Existing Logic: Lecture Navigation State ---
    const [lectureViewMode, setLectureViewMode] = useState('PAPERS_ROOT'); 
    const [currentPaper, setCurrentPaper] = useState(null); 
    const [currentFolder, setCurrentFolder] = useState(null); 
    
    // --- Existing Logic: Homework Navigation State ---
    const [homeworkViewMode, setHomeworkViewMode] = useState('PAPERS_ROOT'); 
    const [currentHomeworkPaper, setCurrentHomeworkPaper] = useState(null); 
    const [currentHomeworkFolder, setCurrentHomeworkFolder] = useState(null);
    const [homeworkList, setHomeworkList] = useState([]);
    const [isLoadingHomework, setIsLoadingHomework] = useState(false);

    // --- Existing Logic: Data State ---
    const [assignedPapersList, setAssignedPapersList] = useState([]); 
    const [lectures, setLectures] = useState([]); 
    const [isLoadingLectures, setIsLoadingLectures] = useState(false);
    
    // --- Existing Logic: Live Session State ---
    const [activeLiveSessionIds, setActiveLiveSessionIds] = useState([]);

    // --- NEW: Report Generation State ---
    const [isGeneratingReport, setIsGeneratingReport] = useState(null); // Track which paper is generating

    // --- Existing Logic: Greeting Logic ---
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 5) return 'Good Evening';
        if (hour < 12) return 'Good Morning';
        if (hour < 18) return 'Good Afternoon';
        return 'Good Evening';
    };

    // --- 1. Fetch List of Assigned Papers (UNCHANGED) ---
    useEffect(() => {
        return subscribeByIds('papers', documentId(), userData.assignedPapers,
            papers => setAssignedPapersList(papers.sort((a, b) => (a.name || '').localeCompare(b.name || ''))),
            () => setModal({ show: true, message: 'Unable to load your subjects. Please check your connection and reload.' }));
    }, [userData.assignedPapers, setModal]);

    // --- 2. Fetch Lectures (UNCHANGED) ---
    useEffect(() => {
        if (dashboardTab === 'lectures' && currentPaper) {
            setIsLoadingLectures(true);
            const q = query(collection(db, "lectures"), where("paperId", "==", currentPaper.id));
            const unsub = onSnapshot(q, (snap) => {
                const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
                data.sort((a, b) => {
                    if (a.type === 'folder' && b.type !== 'folder') return -1;
                    if (a.type !== 'folder' && b.type === 'folder') return 1;
                    return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
                });
                setLectures(data);
                setIsLoadingLectures(false);
            }, (error) => {
                console.error("Error fetching lectures:", error);
                setIsLoadingLectures(false);
            });
            return () => unsub();
        } else {
            setLectures([]);
        }
    }, [dashboardTab, currentPaper]);

    // --- 3. Fetch Homework (UNCHANGED) ---
    useEffect(() => {
        if (dashboardTab === 'homeworks' && currentHomeworkPaper) {
            setIsLoadingHomework(true);
            const q = query(collection(db, "homeworks"), where("paperId", "==", currentHomeworkPaper.id));
            const unsub = onSnapshot(q, (snap) => {
                const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
                data.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
                setHomeworkList(data);
                setIsLoadingHomework(false);
            }, (error) => {
                console.error("Error fetching homework:", error);
                setIsLoadingHomework(false);
            });
            return () => unsub();
        } else {
            setHomeworkList([]);
        }
    }, [dashboardTab, currentHomeworkPaper]);

    // --- 4. Listen for Active Live Sessions (UNCHANGED) ---
    useEffect(() => {
        if (dashboardTab === 'lectures') {
            const q = query(collection(db, "liveSessions"), where("isActive", "==", true));
            const unsub = onSnapshot(q, (snap) => {
                const activeIds = snap.docs.map(doc => doc.id);
                setActiveLiveSessionIds(activeIds);
            });
            return () => unsub();
        }
    }, [dashboardTab]);

    // --- UPDATED: Hybrid Report Generation Handler (RETROSPECTIVE & AI INTEGRATED) ---
    const handleDownloadComprehensiveReport = async (paper) => {
        setIsGeneratingReport(paper.id);
        try {
            // STEP 1: Fetch all exams associated with this paper to build an ID list
            const examsQuery = query(collection(db, "exams"), where("paperId", "==", paper.id));
            const examsSnapshot = await getDocs(examsQuery);
            const examsMap = {};
            const validExamIds = [];

            examsSnapshot.docs.forEach(d => {
                const data = { id: d.id, ...d.data() };
                examsMap[d.id] = data;
                validExamIds.push(d.id);
            });

            if (validExamIds.length === 0) {
                setModal({ show: true, message: "No exams found for this syllabus syllabus." });
                return;
            }

            // STEP 2: Fetch attempts using examId list (bypasses missing paperId in legacy documents)
            const attemptsQuery = query(
                collection(db, "attempts"), 
                where("userId", "==", user.uid), 
                where("examId", "in", validExamIds)
            );
            
            const snapshot = await getDocs(attemptsQuery);
            const rawAttempts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filter for finished exams only
            const completedAttempts = rawAttempts.filter(a => a.submittedAt !== null);

            if (completedAttempts.length === 0) {
                setModal({ show: true, message: "No submitted attempts found for this paper. Please complete an exam to generate your analysis." });
                return;
            }

            // STEP 3: Data Normalization (Recovering missing snapshots from master exams via Version History / JSON Fallback)
            const normalizedAttempts = await Promise.all(completedAttempts.map(async (attempt) => {
                let questions = attempt.examQuestions || [];
                
                // If attempt is a legacy record missing the question array, pull from exam template
                if (questions.length === 0 && examsMap[attempt.examId]) {
                    const examData = examsMap[attempt.examId];
                    
                    if (examData.questions && Array.isArray(examData.questions) && examData.questions.length > 0) {
                        // Old system: questions stored directly in Firestore document
                        questions = examData.questions;
                    } else if (attempt.questionsUrl || examData.publishedQuestionsUrl || examData.questionsUrl) {
                        // New system: questions stored in Storage JSON
                        try {
                            // Prioritize specific attempt URL, then published active URL, then working draft URL
                            const targetUrl = attempt.questionsUrl || examData.publishedQuestionsUrl || examData.questionsUrl;
                            const res = await fetch(targetUrl);
                            if (res.ok) {
                                const fetchedQs = await res.json();
                                if (Array.isArray(fetchedQs)) questions = fetchedQs;
                            } else {
                                console.error(`Failed to fetch questions JSON: ${res.status}`);
                            }
                        } catch (err) {
                            console.error("Error fetching questions JSON for report generation:", err);
                        }
                    }
                }

                // Ensure it's an array to prevent .map crashes
                if (!Array.isArray(questions)) {
                    questions = [];
                }

                return {
                    ...attempt,
                    paperId: paper.id, // Ensure consistency for analytics engine
                    examQuestions: questions.map(q => ({
                        ...q,
                        // Tag legacy questions without topics
                        syllabusArea: q.syllabusArea || "General Knowledge (Legacy Record)"
                    }))
                };
            }));

            // STEP 4: Integrate AI Analysis
            // A. Generate the structured tutor prompt based on student mistake patterns
            const { prompt } = generateHybridAnalysisContext(paper, normalizedAttempts);

            // B. Request personalized insights from the InstilAI Cloud Function
            const aiInsights = await fetchAiTutorInsights(prompt);

            // C. Generate the PDF Report (Including AI insights as the 4th argument)
            await downloadStudentAnalysisReport(paper, normalizedAttempts, userData, aiInsights);
            
            setModal({ show: true, message: `Your cumulative analysis for ${paper.name} is ready, featuring personalized InstilAI insights.` });
        } catch (error) {
            console.error("Report System Failure:", error);
            setModal({ show: true, message: "Technical Compiling Error: " + error.message });
        } finally {
            setIsGeneratingReport(null);
        }
    };

    // --- Helper Functions (UNCHANGED) ---
    const getCurrentViewItems = () => {
        const targetParent = currentFolder ? currentFolder.id : null;
        return lectures.filter(item => (item.parentId || null) === targetParent);
    };

    const getCurrentHomeworkItems = () => {
        const targetParent = currentHomeworkFolder ? currentHomeworkFolder.id : null;
        return homeworkList.filter(item => (item.parentId || null) === targetParent);
    };

    // --- Navigation Handlers (UNCHANGED) ---
    const handlePaperClick = (paper) => {
        setCurrentPaper(paper);
        setCurrentFolder(null); 
        setLectureViewMode('PAPER_CONTENTS');
    };

    const handleFolderClick = (folder) => {
        setCurrentFolder(folder);
    };

    const handleBackClick = () => {
        if (currentFolder) {
            if (currentFolder.parentId) {
                const parent = lectures.find(l => l.id === currentFolder.parentId);
                setCurrentFolder(parent || null);
            } else {
                setCurrentFolder(null); 
            }
        } else {
            setLectureViewMode('PAPERS_ROOT');
            setCurrentPaper(null);
        }
    };

    const handleOpenLecture = (lecture) => navigate(`/lecture/${lecture.id}`);

    const handleHomeworkPaperClick = (paper) => {
        setCurrentHomeworkPaper(paper);
        setCurrentHomeworkFolder(null);
        setHomeworkViewMode('PAPER_CONTENTS');
    };

    const handleHomeworkFolderClick = (folder) => setCurrentHomeworkFolder(folder);

    const handleHomeworkBackClick = () => {
        if (currentHomeworkFolder) {
            if (currentHomeworkFolder.parentId) {
                const parent = homeworkList.find(l => l.id === currentHomeworkFolder.parentId);
                setCurrentHomeworkFolder(parent || null);
            } else {
                setCurrentHomeworkFolder(null); 
            }
        } else {
            setHomeworkViewMode('PAPERS_ROOT');
            setCurrentHomeworkPaper(null);
        }
    };

    const handleOpenHomework = (hw) => navigate(`/homework/${hw.id}`);

    const handleStartExam = (exam, startMode) => {
        setActiveExam(exam);
        if (startMode === 'waiting') setView('waiting'); 
        else navigate(`/exam/${exam.id}`);
    };
    
    const handleWaitingRoomStart = () => { if (activeExam) navigate(`/exam/${activeExam.id}`); };

    const handleReviewSubmission = (exam, result) => navigate(`/exam/${exam.id}/result/${result.id}`);

    // --- UI VIEW RENDERING ---

    if (view === 'waiting') {
        return <WaitingRoom exam={activeExam} onStart={handleWaitingRoomStart} user={user} userData={userData} />;
    }

    const currentLectureItems = getCurrentViewItems();
    const currentHomeworkItems = getCurrentHomeworkItems();

    return (
        <div className="student-dashboard min-h-screen bg-[#050505] text-white selection:bg-purple-500/30 overflow-x-hidden">
            
            {/* CINEMATIC HERO SECTION */}
            <header className="student-hero relative min-h-[300px] w-full flex items-center px-10 overflow-hidden">

                <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="relative z-20 max-w-4xl"
                >
                    <span className="text-xs uppercase tracking-[0.4em] font-bold text-gray-500 mb-4 block">YOUR LEARNING SPACE</span>
                    <h1 className="text-4xl md:text-5xl font-serif italic mb-6 leading-tight">
                        {getGreeting()}, <br/>
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500 not-italic font-sans font-black tracking-tighter">
                            {userData.name || user.displayName || 'Scholar'}
                        </span>
                    </h1>
                    <p className="text-gray-400 text-lg md:text-xl font-light tracking-wide max-w-xl">
                        One focused session at a time. Pick up a subject, build your confidence, and get exam-ready.
                    </p>
                    
                    {/* --- NEW: SYSTEM DIAGNOSTICS BUTTON --- */}
                    <button 
                        onClick={() => navigate('/system-requirements')}
                        className="mt-8 inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 bg-black/40 backdrop-blur-sm px-8 py-4 hover:bg-white hover:text-black hover:border-white transition-all duration-500 group"
                    >
                        <span>Check exam readiness</span>
                        <span className="text-purple-500 group-hover:text-black transition-colors">→</span>
                    </button>
                    
                </motion.div>

                <div className="absolute top-10 right-10 z-30 scale-125">
                    <NotificationBell assignedPapers={userData.assignedPapers || []} isDark={true} />
                </div>
            </header>

            {/* FLOATING NAVIGATION TABS */}
            <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-2xl border-y border-white/5 py-4">
                <div className="max-w-7xl mx-auto px-10 flex items-center justify-between">
                    <div className="flex gap-12">
                        {['exams', 'lectures', 'homeworks'].map((tab) => (
                            <button 
                                key={{ exams: 'Exams', lectures: 'Learn & practice', homeworks: 'Assignments' }[tab]}
                                onClick={() => setDashboardTab(tab)}
                                className={`text-[10px] uppercase tracking-[0.3em] font-bold transition-all relative py-2 ${
                                    dashboardTab === tab ? 'text-white' : 'text-gray-600 hover:text-white'
                                }`}
                            >
                                {{ exams: 'Exams', lectures: 'Learn & practice', homeworks: 'Assignments' }[tab]}
                                {dashboardTab === tab && (
                                    <motion.div 
                                        layoutId="activeTabUnderline"
                                        className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-purple-600 to-pink-600 shadow-[0_0_10px_rgba(192,38,211,0.5)]"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </nav>

            {/* MAIN CONTENT AREA */}
            <main className="max-w-7xl mx-auto px-5 md:px-10 py-8 min-h-[60vh]">
                
                <StudyPlan key={user.uid} userId={user.uid} papers={assignedPapersList} onPractice={id => navigate(`/iq-hub/${id}`)} />
                <AnimatePresence mode="wait">
                    {/* --- EXAMS TAB CONTENT --- */}
                    {dashboardTab === 'exams' && (
                        <motion.div 
                            key="exams"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <div className="flex items-end justify-between mb-16 border-b border-white/10 pb-8">
                                <h2 className="text-4xl font-serif italic">Your exams</h2>
                                <p className="text-xs text-gray-500 tracking-widest uppercase">Practice. Reflect. Improve.</p>
                            </div>
                            <ExamList 
                                onStartExam={handleStartExam} 
                                onReviewSubmission={handleReviewSubmission}
                                userData={userData} 
                                user={user} 
                                isCinematic={true} 
                            />
                        </motion.div>
                    )}

                    {/* --- LECTURES TAB CONTENT (INCLUDING KNOWLEDGE LIBRARY) --- */}
                    {dashboardTab === 'lectures' && (
                        <motion.div 
                            key="lectures"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="animate-fade-in"
                        >
                            {/* Breadcrumbs Header */}
                            <div className="flex justify-between items-center mb-16">
                                <div>
                                    <h2 className="text-4xl font-serif italic">
                                        {lectureViewMode === 'PAPERS_ROOT' ? 'Knowledge Library' : currentPaper.name}
                                    </h2>
                                    <div className="flex gap-2 text-[10px] uppercase tracking-widest text-purple-500 mt-2 font-bold">
                                        <span>Portal</span>
                                        {lectureViewMode === 'PAPER_CONTENTS' && (
                                            <>
                                                <span className="text-gray-700">/</span>
                                                <span className="text-white">{currentPaper.name}</span>
                                                {currentFolder && (
                                                    <>
                                                        <span className="text-gray-700">/</span>
                                                        <span className="text-white">{currentFolder.title}</span>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {lectureViewMode === 'PAPER_CONTENTS' && (
                                    <button 
                                        onClick={handleBackClick} 
                                        className="text-[10px] uppercase tracking-widest border border-white/20 px-8 py-3 hover:bg-white hover:text-black transition-all duration-500 font-bold"
                                    >
                                        &larr; Return
                                    </button>
                                )}
                            </div>

                            {/* LECTURE VIEWS (CARDS) */}
                            {lectureViewMode === 'PAPERS_ROOT' ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                                    {assignedPapersList.length > 0 ? (
                                        assignedPapersList.map(paper => (
                                            <div 
                                                key={paper.id} 
                                                className="group relative h-auto min-h-96 bg-[#111] overflow-hidden border border-white/5 pb-6"
                                            >
                                                {/* Background Elements */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent z-10" />
                                                <div className="absolute inset-0 bg-purple-900/10 group-hover:bg-purple-900/30 transition-all duration-700" />
                                                
                                                {/* Card Content */}
                                                <div className="relative pt-44 px-10 z-20 w-full">
                                                    <p className="text-[9px] uppercase tracking-[0.4em] text-purple-400 font-bold mb-2">Academic Paper</p>
                                                    <h3 className="text-3xl font-bold uppercase tracking-tighter mb-6">{paper.name}</h3>
                                                    
                                                    {/* HYBRID ACTION BUTTONS */}
                                                    <div className="flex flex-col gap-3">
                                                        <button 
                                                            onClick={() => handlePaperClick(paper)}
                                                            className="w-full text-left text-[9px] uppercase tracking-widest bg-white text-black font-black py-3 px-6 hover:bg-purple-500 hover:text-white transition-colors"
                                                        >
                                                            Enter Syllabus Rooms →
                                                        </button>

                                                        {/* --- INTEGRATED: INSTIL IQ PRACTICE ARENA LAUNCHER --- */}
                                                        <button 
                                                            onClick={() => navigate(`/iq-hub/${paper.id}`)}
                                                            className="w-full text-left text-[9px] uppercase tracking-widest bg-gradient-to-r from-[#D02C70] to-[#F05530] text-white font-black py-3 px-6 hover:opacity-90 shadow-md transition-all"
                                                        >
                                                            🧠 Launch Instil IQ Practice Area →
                                                        </button>
                                                        
                                                        <button onClick={() => navigate(`/tracker/${encodeURIComponent(paper.id)}`)} className="w-full text-left text-[9px] uppercase tracking-widest border border-pink-400/50 text-pink-300 font-bold py-3 px-6 hover:bg-pink-500/10 transition-colors">📅 My daily IQ tracker →</button>
                                                        {/* --- RETROSPECTIVE COMPATIBLE DOWNLOAD BUTTON --- */}
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownloadComprehensiveReport(paper);
                                                            }}
                                                            disabled={isGeneratingReport === paper.id}
                                                            className="w-full text-left text-[9px] uppercase tracking-widest border border-white/10 text-gray-400 font-bold py-3 px-6 hover:border-purple-500 hover:text-purple-400 transition-all disabled:opacity-50"
                                                        >
                                                            {isGeneratingReport === paper.id ? 'Recovering History...' : 'Download Analysis (Incl. History) ↓'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="col-span-full border border-dashed border-white/10 p-20 text-center opacity-40">
                                            No papers assigned.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                    {!isLoadingLectures && !currentLectureItems.length && <p className="p-6">No lessons here yet. Check back soon.</p>}
                                    {isLoadingLectures ? <Skeleton type="card" count={4} isDark={true} /> : (
                                        currentLectureItems.map(item => {
                                            const isLive = activeLiveSessionIds.includes(item.id);
                                            return (
                                                <div 
                                                    key={item.id} 
                                                    onClick={() => item.type === 'folder' ? handleFolderClick(item) : handleOpenLecture(item)} 
                                                    className={`group relative aspect-[3/4] bg-white/[0.03] border transition-all duration-700 overflow-hidden flex flex-col items-center justify-center p-8 ${
                                                        isLive ? 'border-red-500/50 shadow-[0_0_30px_rgba(239,68,68,0.1)]' : 'border-white/5 hover:border-purple-500/50'
                                                    }`}
                                                >
                                                    {isLive && (
                                                        <div className="absolute top-6 left-6 flex items-center gap-2">
                                                            <div className="h-2 w-2 rounded-full bg-red-600 animate-ping" />
                                                            <span className="text-[8px] font-black tracking-widest uppercase">Live Session</span>
                                                        </div>
                                                    )}
                                                    <div className="text-5xl opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 mb-6">
                                                        {item.type === 'folder' ? '📁' : '📄'}
                                                    </div>
                                                    <h3 className="text-center text-sm font-bold uppercase tracking-widest leading-relaxed">
                                                        {item.title}
                                                    </h3>
                                                    <div className="mt-8 flex items-center gap-4 text-[9px] uppercase tracking-widest text-gray-500 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <span>View Detail</span>
                                                        <span className="text-purple-500">→</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* --- HOMEWORKS TAB CONTENT --- */}
                    {dashboardTab === 'homeworks' && (
                        <motion.div 
                            key="homeworks"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="animate-fade-in"
                        >
                            <div className="flex items-end justify-between mb-16 border-b border-white/10 pb-8">
                                <h2 className="text-4xl font-serif italic uppercase tracking-tighter">Tasks & Assignments</h2>
                                <button className="study-secondary" onClick={handleHomeworkBackClick} disabled={homeworkViewMode === 'PAPERS_ROOT'}>← Back to subjects</button>
                            </div>

                            {homeworkViewMode === 'PAPERS_ROOT' ? (
                                <div className="space-y-[1px] bg-white/5 border border-white/5">
                                    {assignedPapersList.map(paper => (
                                        <div 
                                            key={paper.id} 
                                            onClick={() => handleHomeworkPaperClick(paper)} 
                                            className="p-12 flex justify-between items-center hover:bg-white/[0.02] transition-colors cursor-pointer group"
                                        >
                                            <h3 className="text-2xl font-light tracking-[0.2em] uppercase group-hover:translate-x-6 transition-transform duration-700">{paper.name}</h3>
                                            <div className="flex items-center gap-4">
                                                <span className="text-[10px] text-gray-500 tracking-[0.3em] font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity">Open Dashboard</span>
                                                <span className="text-xl group-hover:text-purple-500 transition-colors">→</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-6">
                                    {!isLoadingHomework && !currentHomeworkItems.length && <p className="p-6">You’re all caught up. No assignments here yet.</p>}
                                    {isLoadingHomework ? <Skeleton type="row" count={4} isDark={true} /> : (
                                        currentHomeworkItems.map(item => (
                                            <div 
                                                key={item.id} 
                                                onClick={() => item.type === 'folder' ? handleHomeworkFolderClick(item) : handleOpenHomework(item)} 
                                                className="group border border-white/5 p-10 flex items-center justify-between hover:bg-gradient-to-r hover:from-purple-900/10 hover:to-transparent transition-all"
                                            >
                                                <div className="flex items-center gap-8">
                                                    <span className="text-4xl opacity-30">{item.type === 'folder' ? '📁' : '📝'}</span>
                                                    <div>
                                                        <h4 className="text-xl font-bold uppercase tracking-tight">{item.title}</h4>
                                                        <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
                                                            {item.createdAt?.seconds ? `Released: ${new Date(item.createdAt.seconds * 1000).toLocaleDateString()}` : 'Date Pending'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button className="text-[9px] font-black uppercase tracking-[0.3em] border border-white/10 px-8 py-3 group-hover:bg-white group-hover:text-black transition-all">
                                                    Begin Now
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            <footer className="py-20 border-t border-white/5 flex flex-col items-center gap-6 opacity-30">
                <div className="h-20 w-[1px] bg-gradient-to-b from-purple-500 to-transparent" />
                <p className="text-[8px] tracking-[1em] uppercase font-black">Instil Learning Hub &copy; 2026</p>
            </footer>
        </div>
    );
};

export default StudentDashboard;